/* eslint-disable react/no-string-refs, react/forbid-prop-types  */
import React, { useEffect, useState, useRef } from 'react';
import { gql } from '@apollo/client';
import { connect } from 'react-redux';
import TypeAhead from '@vrbo/ferris-typeahead';
import { SVGIcon } from '@homeaway/react-svg';
import { LOCATION__24 } from '@homeaway/svg-defs';
import PropTypes from 'prop-types';
import { getVrboCompatibleAddress } from '@homeaway/google-address-parser';
import TranslationUtil from '../../../../utils/TranslationUtil';
import LocationActions from '../../../../actions/location';
import GoogleActions from '../../../../actions/google';
import log from '../../../../utils/ClientLogger';
import { retrieveData } from '../../../../actions/graphQlSvc';
import ExperimentUtil from '../../../../utils/ExperimentUtil';
import { isClAddressImprovements, isGeocoderEnabled, usesGeoFeed } from '../../../../utils/ExperimentsUtil';
import { LODGIN_TYPES } from '../../../../actions/classification';


const TYPEAHEAD_DEBOUNCE_MS = 250;
const regionWithAreaLevel2 = ['ES', 'IT', 'FR'];
const GLANCE_MASKED = { glance_masked: 'true' };

// setLatLng parameter is set to false whenever the geocoder experiment is on
const innerSetAddress = (placeDetails, setField, setLatLng, useLatin, useGeoFeeds) => {
    const parsedAddress = getVrboCompatibleAddress(placeDetails);
    if (useGeoFeeds) {
        const keyToLook = regionWithAreaLevel2.includes(parsedAddress.country)
            ? 'administrative_area_level_2'
            : 'administrative_area_level_1';
        parsedAddress.stateProvince = placeDetails.address_components
            .find(component => component.types[0] === keyToLook).long_name;
    }
    parsedAddress.address2 = '';
    Object.keys(parsedAddress).forEach((k) => {
        // the latlng is passed whenever the location validation experiment is on
        if (!setLatLng) {
            setField(k, parsedAddress[k], useLatin, {
                lat: placeDetails.geometry.location.lat(),
                lng: placeDetails.geometry.location.lng()
            });
        } else {
            setField(k, parsedAddress[k], useLatin);
        }
    });
    if (setLatLng) {
        setLatLng({
            lat: placeDetails.geometry.location.lat(),
            lng: placeDetails.geometry.location.lng()
        });
    }
};

export const HAS_UNICODE_ADDRESS = gql`
    query ($placeId: String!) {
        hasUnicodeAddress(placeId: $placeId)
    }`;

export const LocationTypeahead = (props) => {
    const {
        mapsApi,
        setTypeaheadQuery,
        setTypeaheadPredictions,
        setLatLng,
        clearAddress,
        typeaheadPredictions,
        typeaheadQuery,
        placeholder,
        setGooglePlaceId,
        setAddressField,
        features,
        isGeoCoderEnabled,
        clearGeocoderLatLng,
        isAddressV2,
        setPropertyName,
        updateUserTypeaheadInput,
        useGeoFeeds
    } = props;

    const [placesService, setPlacesService] = useState();
    const [autoCompleteService, setAutoCompleteService] = useState();
    const placesContainer = useRef();
    const autocompleteTimeout = useRef();

    const onTypeAheadChange = (e) => {
        const input = e;
        updateUserTypeaheadInput(input);

        clearAddress();
        setTypeaheadPredictions([]);
        if (!isGeoCoderEnabled) setLatLng({});
        if (isGeoCoderEnabled) clearGeocoderLatLng();

        setTypeaheadQuery(input);
        if (autocompleteTimeout) {
            clearTimeout(autocompleteTimeout.current);
            autocompleteTimeout.current = null;
        }
        if (input) {
            if (autoCompleteService) {
                const request = {
                    input
                };

                autocompleteTimeout.current = setTimeout(() => {
                    autoCompleteService.getPlacePredictions(request, (predictions, status) => {
                        if (status !== 'OK') {
                            log('warn', 'google getPlacePredictions returned an error',
                                {
                                    request,
                                    status
                                });
                        }
                        if (predictions) {
                            setTypeaheadPredictions(predictions.map((prediction => ({
                                key: prediction.id,
                                placeId: prediction.place_id,
                                text: prediction.description
                            }))));
                        }
                    });
                }, TYPEAHEAD_DEBOUNCE_MS);
            }
        }
    };

    function onTypeAheadSelection(place) {
        if (!place || !place.placeId) {
            return; // nothing we can do with that.
        }
        const { placeId } = place;

        clearAddress();
        if (isAddressV2) clearGeocoderLatLng();

        const apiFieldsParam = ['geometry', 'formatted_address', 'address_components'];
        if (isAddressV2 || features.isConventionalLodgingFlow) apiFieldsParam.push('name');

        if (placesService) {
            placesService.getDetails({
                placeId,
                fields: apiFieldsParam
            }, async (placeDetails, status) => {
                if (status !== 'OK') {
                    log('warn', 'google placeService.getDetails returned an error',
                        {
                            placeId,
                            status
                        });
                    return; // placeDetails will be undefined (or nUL)
                }
                const variables = {
                    placeId
                };
                // eslint-disable-next-line max-len
                if (isAddressV2 || features.isConventionalLodgingFlow) setPropertyName(placeDetails.name);
                if (features.isConventionalLodgingFlow) {
                    const unicodeData = await retrieveData(HAS_UNICODE_ADDRESS, variables);
                    // eslint-disable-next-line max-len
                    const useLatin = unicodeData && unicodeData.data && unicodeData.data.hasUnicodeAddress;
                    // eslint-disable-next-line max-len
                    innerSetAddress(placeDetails, setAddressField, !isGeoCoderEnabled ? setLatLng : false, useLatin, useGeoFeeds);
                } else {
                    // eslint-disable-next-line max-len
                    innerSetAddress(placeDetails, setAddressField, !isGeoCoderEnabled ? setLatLng : false, false, useGeoFeeds);
                }
                log('info', 'location - autocomplete prediction selected',
                    {
                        placeDetails,
                        typeaheadQuery
                    });
                updateUserTypeaheadInput(typeaheadQuery);
                setTypeaheadQuery(placeDetails.formatted_address);
                setGooglePlaceId(placeId);
            });
        }
    }

    useEffect(() => {
        if (mapsApi && (typeof mapsApi === 'object' && Object.keys(mapsApi).length > 0)) {
            setPlacesService(new mapsApi.places.PlacesService(placesContainer.current));
            setAutoCompleteService(new mapsApi.places.AutocompleteService());
        }
    }, [mapsApi]);


    return (
        <div className="ulx-location-typeahead" data-wdio="type-ahead-container" {...GLANCE_MASKED}>
            <div ref={placesContainer} />
            <TypeAhead
                label={placeholder}
                id="locationTypeAhead"
                predictions={typeaheadPredictions}
                onChange={onTypeAheadChange}
                onEnterKey={() => {}}
                onEscapeKey={() => { }}
                onSelection={onTypeAheadSelection}
                defaultValue={typeaheadQuery}
                className="typeahead-field"
                useBolding={false}
            />
            <SVGIcon className="ulx-location-pin" markup={LOCATION__24} />
        </div>
    );
};

LocationTypeahead.propTypes = {
    mapsApi: PropTypes.shape({
        places: PropTypes.shape({
            AutocompleteService: PropTypes.any,
            PlacesService: PropTypes.any
        })
    }),
    setTypeaheadQuery: PropTypes.func.isRequired,
    setTypeaheadPredictions: PropTypes.func.isRequired,
    setLatLng: PropTypes.func.isRequired,
    typeaheadPredictions: PropTypes.arrayOf(PropTypes.object),
    typeaheadQuery: PropTypes.string,
    placeholder: PropTypes.string.isRequired,
    clearAddress: PropTypes.func.isRequired,
    setGooglePlaceId: PropTypes.func.isRequired,
    setAddressField: PropTypes.func.isRequired,
    addressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            isSet: PropTypes.bool.isRequired,
            error: PropTypes.string
        })
    }).isRequired,
    config: PropTypes.shape({
        region: PropTypes.string.isRequired,
        lang: PropTypes.string.isRequired
    }).isRequired,
    features: PropTypes.shape({
        isLatinInputEnabled: PropTypes.bool,
        isConventionalLodgingFlow: PropTypes.bool
    }).isRequired,
    clearGeocoderLatLng: PropTypes.func.isRequired,
    isGeoCoderEnabled: PropTypes.bool.isRequired,
    isAddressV2: PropTypes.bool.isRequired,
    setPropertyName: PropTypes.func.isRequired,
    updateUserTypeaheadInput: PropTypes.func.isRequired,
    useGeoFeeds: PropTypes.bool.isRequired
};

LocationTypeahead.defaultProps = {
    mapsApi: {},
    typeaheadPredictions: [],
    typeaheadQuery: undefined
};

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const { app, location } = state;
    const { features } = app;
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const getTranslation = suffix => (state.classification.lodgingPreference === LODGIN_TYPES.CL ? translations.getTranslation(`lyp.locationTypeahead.${suffix}`) : translations.getTranslation(`lyp.locationTypeahead.${suffix}.vr`));
    const {
        typeaheadQuery,
        typeaheadPredictions,
        address,
        latLng,
        addressValidation
    } = location;
    const experimentUtil = new ExperimentUtil(app.experiments);
    const isGeoCoderEnabled = isGeocoderEnabled(experimentUtil);
    const isAddressV2 = isClAddressImprovements(experimentUtil);
    const useGeoFeeds = usesGeoFeed(experimentUtil);

    return {
        isGeoCoderEnabled,
        useGeoFeeds,
        placeholder: getTranslation('placeholder'),
        typeaheadQuery,
        typeaheadPredictions,
        address,
        latLng,
        features,
        addressValidation,
        isAddressV2
    };
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    setTypeaheadQuery: query => dispatch(LocationActions.setTypeaheadQuery(query)),
    setTypeaheadPredictions: predictions => dispatch(
        LocationActions.setTypeaheadPredictions(predictions)
    ),
    setLatLng: latLong => dispatch(LocationActions.setLatLng(latLong)),
    setAddressField: (fieldName, value, useLatin, candidateLatLong) => {
        dispatch(LocationActions.setAddressField(fieldName, value, useLatin, candidateLatLong));
    },
    clearAddress: () => {
        dispatch(LocationActions.clearAddress());
        dispatch(GoogleActions.clearGooglePlaceId());
    },
    setGooglePlaceId: (placeId) => {
        dispatch(GoogleActions.setGooglePlaceId(placeId));
    },
    clearGeocoderLatLng: () => {
        dispatch(LocationActions.clearGeocoderLatLng());
    },
    setPropertyName: (name) => {
        dispatch(LocationActions.setPropertyName(name));
    }
});

const ConnectedLocationTypeahead = connect(mapStateToProps, mapDispatchToProps)(LocationTypeahead);
ConnectedLocationTypeahead.displayName = 'LocationTypeahead';
export default ConnectedLocationTypeahead;
